import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Trash, ArrowsClockwise, TrashSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import NotesTable from "@/components/pinboard/NotesTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeletedNotesDialog({ open, onOpenChange, onRestored }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // PIN-gated restore confirmation
  const [restoreTarget, setRestoreTarget] = useState(null); // note object or null
  const [pinValue, setPinValue] = useState("");
  const [pinError, setPinError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/deleted-notes`);
      setItems(data.items || []);
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const askRestore = (note) => {
    setPinValue("");
    setPinError("");
    setRestoreTarget(note);
  };

  const cancelRestore = () => {
    setRestoreTarget(null);
    setPinValue("");
    setPinError("");
  };

  const confirmRestore = async () => {
    if (pinValue !== "2650") {
      setPinError("ΛΑΘΟΣ ΚΩΔΙΚΟΣ");
      return;
    }
    const id = restoreTarget?.id;
    setRestoreTarget(null);
    setPinValue("");
    setPinError("");
    try {
      await axios.post(`${API}/admin/deleted-notes/${id}/restore`);
      toast.success("ΕΠΑΝΑΦΕΡΘΗΚΕ");
      load();
      onRestored && onRestored();
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ");
    }
  };

  const hardDelete = async (id) => {
    if (!window.confirm("ΟΡΙΣΤΙΚΗ ΔΙΑΓΡΑΦΗ;")) return;
    try {
      await axios.delete(`${API}/admin/deleted-notes/${id}`);
      toast.success("ΔΙΑΓΡΑΦΗΚΕ ΟΡΙΣΤΙΚΑ");
      load();
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ");
    }
  };

  const emptyAll = async () => {
    if (!items.length) return;
    if (!window.confirm(`ΟΡΙΣΤΙΚΗ ΔΙΑΓΡΑΦΗ ΟΛΩΝ (${items.length});`)) return;
    try {
      await axios.delete(`${API}/admin/deleted-notes`);
      toast.success("ΑΔΕΙΑΣΤΗΚΕ");
      load();
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[1100px] rounded-2xl p-0 overflow-hidden border-none max-h-[92vh] overflow-y-auto"
        data-testid="deleted-dialog"
      >
        <div className="px-7 pt-6 pb-3 bg-ink text-white">
          <DialogHeader>
            <DialogTitle className="font-caveat text-3xl font-bold flex items-center gap-2">
              <TrashSimple size={24} weight="bold" />
              ΔΙΕΓΡΑΜΕΝΑ
            </DialogTitle>
            <DialogDescription className="text-white/70 text-[10px] uppercase tracking-[0.2em] font-bold">
              ΠΙΝΑΚΑΣ — ΚΛΙΚ ΣΤΗΝ ΕΠΙΚΕΦΑΛΙΔΑ ΓΙΑ ΤΑΞΙΝΟΜΗΣΗ
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-7 py-5 bg-white">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/70">
              {items.length} ΕΓΓΡΑΦΕΣ
            </div>
            {items.length > 0 && (
              <button
                onClick={emptyAll}
                data-testid="deleted-empty-all"
                className="text-[10px] uppercase tracking-wider font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors"
              >
                ΑΔΕΙΑΣΜΑ ΟΛΩΝ
              </button>
            )}
          </div>

          {loading ? (
            <p className="text-xs text-ink/50 italic">ΦΟΡΤΩΣΗ...</p>
          ) : (
            <NotesTable
              notes={items}
              variant="deleted"
              actions={(n) => (
                <>
                  <button
                    onClick={() => askRestore(n)}
                    data-testid={`deleted-restore-${n.id}`}
                    className="p-1.5 rounded-md text-green-700 hover:bg-green-50 transition-colors"
                    title="ΕΠΑΝΑΦΟΡΑ"
                  >
                    <ArrowsClockwise size={14} weight="bold" />
                  </button>
                  <button
                    onClick={() => hardDelete(n.id)}
                    data-testid={`deleted-hard-${n.id}`}
                    className="p-1.5 rounded-md text-red-600/70 hover:text-red-700 hover:bg-red-50 transition-colors"
                    title="ΟΡΙΣΤΙΚΗ ΔΙΑΓΡΑΦΗ"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </>
              )}
            />
          )}
        </div>

        {/* PIN-gated restore confirmation */}
        {restoreTarget && (
          <div
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
            data-testid="restore-pin-modal"
          >
            <div className="w-[420px] max-w-[90vw] rounded-2xl bg-white shadow-2xl overflow-hidden">
              <div className="px-6 py-4 bg-ink text-white">
                <div className="font-caveat text-2xl font-bold flex items-center gap-2">
                  <ArrowsClockwise size={20} weight="bold" />
                  ΕΠΑΝΑΦΟΡΑ ΕΓΓΡΑΦΗΣ
                </div>
                <div className="text-white/70 text-[10px] uppercase tracking-[0.2em] font-bold mt-1">
                  ΚΩΔΙΚΟΣ ΑΣΦΑΛΕΙΑΣ
                </div>
              </div>
              <div className="px-6 py-5 space-y-4">
                <p className="text-sm text-ink/80 font-arial">
                  ΕΠΑΝΑΦΟΡΑ ΤΗΣ ΕΓΓΡΑΦΗΣ{" "}
                  <span className="font-bold">
                    #{restoreTarget.seq || restoreTarget.id?.slice(0, 6)}
                    {restoreTarget?.data?.customer
                      ? ` — ${restoreTarget.data.customer}`
                      : ""}
                  </span>
                  ;
                </p>
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-ink/60 mb-1.5">
                    PIN
                  </label>
                  <input
                    type="password"
                    inputMode="numeric"
                    autoFocus
                    value={pinValue}
                    onChange={(e) => {
                      setPinValue(e.target.value.replace(/\D/g, "").slice(0, 4));
                      if (pinError) setPinError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") confirmRestore();
                      if (e.key === "Escape") cancelRestore();
                    }}
                    data-testid="restore-pin-input"
                    className="w-full rounded-xl bg-white border-2 border-ink/15 focus:border-ink/50 outline-none font-arial text-xl tabular-nums tracking-[0.4em] text-center px-3 py-2.5"
                    placeholder="••••"
                  />
                  {pinError && (
                    <div
                      className="mt-2 text-xs font-bold text-red-600"
                      data-testid="restore-pin-error"
                    >
                      {pinError}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    onClick={cancelRestore}
                    data-testid="restore-pin-cancel"
                    className="px-4 py-2 rounded-xl text-[10px] uppercase tracking-wider font-bold text-ink/70 hover:bg-black/5 transition-colors"
                  >
                    ΑΚΥΡΟ
                  </button>
                  <button
                    onClick={confirmRestore}
                    data-testid="restore-pin-confirm"
                    className="px-4 py-2 rounded-xl text-[10px] uppercase tracking-wider font-bold bg-green-600 text-white hover:bg-green-700 transition-colors"
                  >
                    ΕΠΑΝΑΦΟΡΑ
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
